// Global chat notifications - works on all pages
import "stylesheets/chat"
import { fetchAPI } from "packs/api";
import { createChatConsumer } from "../channels/consumer"
import { emojis } from "redactor/emoji_icons";
import { emojiMap } from "./common";

let lastRange = null;

// Sanitize message HTML - allows basic formatting but escapes dangerous content
function sanitizeMessageHTML(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;

  // Remove script tags and dangerous elements
  div.querySelectorAll('script, style, iframe, object, embed, form, link').forEach(el => el.remove());
  div.querySelectorAll('*').forEach(el => {
    // Remove all event handlers and dangerous attributes
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith('on') || attr.value.toLowerCase().includes('javascript:')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return div.innerHTML;
}

// Global function to save caret range
function saveCaretPosition(editableDiv) {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    if (editableDiv.contains(range.commonAncestorContainer)) {
      lastRange = range;
    }
  }
}

let globalUserSubscription = null;
let globalConsumer = null;
let globalChatSubscriptions = {};
let activeNotifications = new Map(); // Track active notifications by conversation ID
let notificationQueue = []; // Queue for managing multiple notifications
let messageTimestamps = new Map(); // Track recent messages to prevent duplicates
const MAX_NOTIFICATIONS = 5;

const GlobalChatNotifications = {
  init: function() {
    // Note: We allow initialization on /chats page to handle notifications from other conversations
    // The notifications will only show for non-active conversations
    
    // Check mobile device and mobile_chat_popup configuration
    const isMobile = this.isMobileDevice();
    const mobileChatPopup = window.appconfig?.pepper_chat?.mobile_chat_popup;
    
    if (isMobile && mobileChatPopup === false) {
      // Global chat notifications disabled for mobile device
      return;
    }
    
    // Only initialize if user is logged in and has chat access
    const userDataElement = document.querySelector(".session_current_user_token");
    if (!userDataElement) {
      // Global chat notifications disabled - user not allowed to chat or no session data
      return;
    }
    
    const accessToken = userDataElement.dataset.session_current_user_token;
    const currentUserId = document.querySelector(".current_user_id")?.dataset.current_user_id;
    const senderChatId = document.querySelector(".session_current_user_id")?.dataset.session_current_user_id;
    
    if (!accessToken || !currentUserId || !senderChatId) {
      // Global chat notifications disabled - missing required chat credentials
      return;
    }
    
    // Store user data globally
    window.GLOBAL_CHAT = {
      senderId: currentUserId,
      senderChatId: senderChatId,
      accessToken: accessToken,
      is_desktop: window.innerWidth >= 768
    };
    
    // Create consumer
    globalConsumer = createChatConsumer(accessToken);
    
    // Initialize notification system (just create container, no popups)
    this.initNotificationSystem();
    
    // Subscribe to user channel for notifications (only for new messages)
    this.subscribeToUserChannel(senderChatId);
  },
  
  initNotificationSystem: function() {
    // Create notification container if it doesn't exist
    if (!document.getElementById('global-chat-notifications-container')) {
      const containerHTML = `
        <div id="global-chat-notifications-container" class="global-chat-notifications-container">
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', containerHTML);
    }
  },
  
  createNotification: function(notificationId, senderData, message, channelId, messageType = 'received') {
    const container = document.getElementById('global-chat-notifications-container');
    if (!container) return null;
    
    // Check if notification already exists for this conversation
    if (activeNotifications.has(channelId)) {
      // Update existing notification with new message
      return this.updateNotification(channelId, message, messageType);
    }
    
    // Remove oldest notification if we're at the limit
    if (activeNotifications.size >= MAX_NOTIFICATIONS) {
      const oldestChannelId = Array.from(activeNotifications.keys())[0];
      this.removeNotification(oldestChannelId);
    }
    
    const directionClass = messageType === 'sent' ? 'sent-message' : 'received-message';
    const directionLabel = messageType === 'sent' ? 'To' : 'From';
    const avatarSrc = senderData.image_url || '/assets/default-avatar.png';
    
    const notificationHTML = `
      <div id="${notificationId}" class="chat-notification" data-channel-id="${channelId}">
        <div class="chat-notification-header">
          <div class="chat-notification-user">
            <img class="chat-notification-avatar" src="${avatarSrc}" alt="">
            <div class="chat-notification-user-info">
              <strong style="margin-right: 8px;"><a href="/chats/${senderData.name}" class="chat-notification-username-link">${senderData.name.length > 15 ? senderData.name.substring(0, 15) + '...' : senderData.name}</a></strong>
              <span class="inline-block mr-2 w-[7px] h-[7px] ${senderData.online ? 'bg-light-secondary' : 'bg-primary-icon-light dark:bg-primary-icon-dark'} rounded-full"></span>
            </div>
          </div>
          <div class="chat-notification-header-actions">
            <button class="chat-notification-minimize" data-notification-id="${notificationId}" title="Minimize">−</button>
            <button class="chat-notification-close" data-notification-id="${notificationId}" title="Close">×</button>
          </div>
        </div>
        <div class="chat-notification-body chat-notification-body-pattern bg-background-shade-light dark:bg-background-shade-dark">
          <div class="chat-notification-messages chat-notification-messages-transparent">
            <div class="chat-message-wrapper ${messageType}">
              <div class="chat-message-bubble ${messageType}">
                <div class="chat-message-content">
                  ${message}
                </div>
                <div class="chat-message-meta">${I18n.t("now")}</div>
              </div>
            </div>
          </div>
          <div class="chat-notification-typing" style="display:none;">
            <span class="typing-text"></span>
          </div>
        </div>
        <div class="chat-notification-footer chat-notification-footer-styled">
          <button type="button" id="chat_smiley_icons_${channelId}" class="chat-emoji-button">
            <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.408 7.5h.01m-6.876 0h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM4.6 11a5.5 5.5 0 0 0 10.81 0H4.6Z"/>
            </svg>
          </button>
          <div class="chat-input-container">
            <div id="chat_smiley_icons_container_${channelId}" class="emoji-picker-container"></div>
            <div contenteditable="true" id="chat_textarea_${channelId}" class="chat-notification-input chat-notification-input-styled" data-placeholder="${I18n.t("enter_your_msg")}" data-notification-id="${notificationId}" role="textbox" aria-label="Your message"></div>
          </div>
          <button id="publish_message_${channelId}" class="chat-notification-send chat-notification-send-styled" data-notification-id="${notificationId}">
            <svg class="w-5 h-5 rotate-90 rtl:-rotate-90" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 18 20">
              <path d="m17.914 18.594-8-18a1 1 0 0 0-1.828 0l-8 18a1 1 0 0 0 1.157 1.376L8 18.281V9a1 1 0 0 1 2 0v9.281l6.758 1.689a1 1 0 0 0 1.156-1.376Z"/>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    container.insertAdjacentHTML('beforeend', notificationHTML);
    
    const notification = document.getElementById(notificationId);
    
    // Add dark mode class if body has dark class
    if (document.body.classList.contains('dark')) {
      notification.classList.add('dark');
    }
    
    // Store notification data
    activeNotifications.set(channelId, {
      id: notificationId,
      element: notification,
      channelId: channelId,
      recipientId: senderData.id,
      recipientName: senderData.name,
      messages: [{ text: message, type: messageType, timestamp: Date.now() }]
    });
    
    // Bind events immediately 
    this.bindNotificationEvents(notification, notificationId, channelId);
    
    // Initialize placeholder for contentEditable input and send button state
    const input = notification.querySelector(`#chat_textarea_${channelId}`);
    const sendButton = notification.querySelector(`#publish_message_${channelId}`);

    if (input && input.contentEditable) {
      if (input.textContent.trim() === "") {
        input.classList.add("placeholder-active");
      }
    }
    
    // Initialize send button as disabled
    if (sendButton) {
      sendButton.disabled = true;
      sendButton.style.filter = "grayscale(1)";
      sendButton.style.opacity = "0.5";
      sendButton.style.cursor = "not-allowed";
    }

    // Load previous messages for this conversation (after events are bound)
    this.loadChatHistory(channelId, senderData);
    
    return notification;
  },
  
  updateNotification: function(channelId, message, messageType) {
    const notificationData = activeNotifications.get(channelId);
    if (!notificationData) return null;
    
    // Check for duplicate messages (same message within 2 seconds)
    const messageKey = `${channelId}-${message.substring(0, 50)}-${messageType}`;
    const now = Date.now();
    const lastMessageTime = messageTimestamps.get(messageKey);
    
    if (lastMessageTime && (now - lastMessageTime) < 2000) {
      // Duplicate message detected, skipping
      return notificationData.element;
    }
    
    // Store message timestamp for deduplication
    messageTimestamps.set(messageKey, now);
    
    // Clean old timestamps (older than 5 seconds)
    for (const [key, time] of messageTimestamps.entries()) {
      if (now - time > 5000) {
        messageTimestamps.delete(key);
      }
    }
    
    const notification = notificationData.element;
    const messagesContainer = notification.querySelector('.chat-notification-messages');
    
    // Add new message with timestamp and proper colors
    const messageHTML = `
      <div class="chat-message-wrapper ${messageType}">
        <div class="chat-message-bubble ${messageType}">
          <div class="chat-message-content">
            ${message}
          </div>
          <div class="chat-message-meta">${I18n.t("now")}</div>
        </div>
      </div>
    `;
    messagesContainer.insertAdjacentHTML('beforeend', messageHTML);
    
    // Update stored data
    notificationData.messages.push({ text: message, type: messageType, timestamp: now });
    
    // Scroll to bottom of messages
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
    
    // Show notification again if it was hidden (maintain flex display)
    notification.style.display = 'flex';
    
    return notification;
  },
  
  bindNotificationEvents: function(notification, notificationId, channelId) {
    const closeBtn = notification.querySelector('.chat-notification-close');
    const minimizeBtn = notification.querySelector('.chat-notification-minimize');
    const sendBtn = notification.querySelector(`#publish_message_${channelId}`);
    const input = notification.querySelector(`#chat_textarea_${channelId}`);
    const usernameLink = notification.querySelector('.chat-notification-username');
    
    // Binding notification events
    
    // Handle username click to open chat page
    if (usernameLink && !usernameLink.dataset.eventsBound) {
      usernameLink.addEventListener('click', (e) => {
        // Close the notification when navigating to chat page
        this.removeNotification(channelId);
      });
      usernameLink.dataset.eventsBound = 'true';
    }
    
    // Initialize emoji functionality using the same system as main chat
    const smileyIconId = `chat_smiley_icons_${channelId}`;
    const smileyContainerId = `chat_smiley_icons_container_${channelId}`;
    const textareaId = `chat_textarea_${channelId}`;
    
    // Initialize emoji system with delay to avoid auto-opening
    setTimeout(() => {
      this.initChatIcons(smileyIconId, smileyContainerId, textareaId);
    }, 500);
    
    // Close notification
    if (closeBtn && !closeBtn.dataset.eventsBound) {
      closeBtn.addEventListener('click', () => {
        this.removeNotification(channelId);
      });
      closeBtn.dataset.eventsBound = 'true';
    }
    
    // Minimize notification (same as close for now)
    if (minimizeBtn && !minimizeBtn.dataset.eventsBound) {
      minimizeBtn.addEventListener('click', () => {
        const body = notification.querySelector('.chat-notification-body');
        const footer = notification.querySelector('.chat-notification-footer');
        const isHidden = body && body.style.display === 'none';
        // The .style property cannot set !important. Use setProperty for this purpose.
        if (body) {
          body.style.setProperty('display', isHidden ? '' : 'none', isHidden ? '' : 'important');
        }
        if (footer) {
          footer.style.setProperty('display', isHidden ? '' : 'none', isHidden ? '' : 'important');
        }
        notification.style.setProperty('height', isHidden ? '' : 'max-content', isHidden ? '' : 'important');
      });
      
      // Mark that events have been bound to prevent duplicate listeners
      minimizeBtn.dataset.eventsBound = 'true';
    }
    
    // Send reply
    const sendReply = () => {
      const text = input.contentEditable ? input.textContent.trim() : input.value.trim();
      if (text.length === 0 && input.innerHTML.length === 0) {
        return;
      }
      
      const message = input.contentEditable ? input.innerHTML : input.value;
      const notificationData = activeNotifications.get(channelId);
      const recipientId = notificationData.recipientId;
      
      if (channelId && globalChatSubscriptions[channelId]) {
        // Use existing subscription
        const platform = window.GLOBAL_CHAT.is_desktop ? "desktop_site" : "mobile_site";
        // Sending message via existing subscription
        globalChatSubscriptions[channelId].sendMessage(message, platform);
        
        // Clear input
        if (input.contentEditable) {
          input.textContent = "";
        } else {
          input.value = '';
        }

        // Update send button state after clearing input
        this.updateSendButtonState(notification, channelId);
      } else {
        // Fetch channel and subscribe first, then send
        fetchAPI("/get_channel/" + recipientId, "GET", {}, "json").then(data => {
          if (data.conversation_id !== undefined) {
            // Subscribe to channel first
            GlobalChatNotifications.subscribeToChannel(data.conversation_id, recipientId);

            // Retry sending message until subscription is ready
            const maxRetries = 10;
            const retryDelay = 100; // ms
            let retryCount = 0;

            const trySendMessage = () => {
              const platform = window.GLOBAL_CHAT.is_desktop ? "desktop_site" : "mobile_site";

              if (globalChatSubscriptions[data.conversation_id]) {
                globalChatSubscriptions[data.conversation_id].sendMessage(message, platform);
              } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(trySendMessage, retryDelay);
              } else {
                // All retries failed - show error to user
                console.error('Failed to send message: subscription not established');
                GlobalChatNotifications.showSendError(channelId, message);
              }
            };

            // Start trying to send
            setTimeout(trySendMessage, retryDelay);

            // Clear input
            if (input.contentEditable) {
              input.textContent = "";
            } else {
              input.value = '';
            }

            // Update send button state after clearing input
            GlobalChatNotifications.updateSendButtonState(notification, data.conversation_id);
          } else {
            // No conversation_id returned - show error
            console.error('Failed to get channel for recipient:', recipientId);
            GlobalChatNotifications.showSendError(channelId, message);
          }
        }).catch(error => {
          console.error('Error fetching channel:', error);
          GlobalChatNotifications.showSendError(channelId, message);
        });
      }
    };
    
    if (sendBtn && !sendBtn.dataset.eventsBound) {
      sendBtn.addEventListener('click', () => {
        sendReply();
        
        // Close emoji picker after sending
        const emojiContainer = notification.querySelector(`#chat_smiley_icons_container_${channelId}`);
        if (emojiContainer) {
          emojiContainer.style.display = 'none';
        }
      });
      sendBtn.dataset.eventsBound = 'true';
    }
    
    // Mark chat as read when user clicks on input
    if (input && !input.dataset.readEventsBound) {
      const markChatAsRead = () => {
        // Mark messages as read via subscription
        if (globalChatSubscriptions[channelId]) {
          globalChatSubscriptions[channelId].markAsRead();
        }
        
        const isOnChatPage = window.location.pathname.includes('/chats');
        const notificationData = activeNotifications.get(channelId);
        const recipientId = notificationData ? notificationData.recipientId : null;
        
        // For non-chat pages, use tracked channel unread count
        if (!isOnChatPage) {
          const trackedUnreadCount = GlobalChatNotifications.channelUnreadCounts && GlobalChatNotifications.channelUnreadCounts[channelId] || 0;
          if (trackedUnreadCount > 0) {
            GlobalChatNotifications.updateHeaderUnreadCount(-trackedUnreadCount);
            GlobalChatNotifications.channelUnreadCounts[channelId] = 0; // Reset
          }
        } else if (recipientId) {
          // On chat page, find chat list item and unread count element
          const chatListItem = document.querySelector(`[data-channel-id="${channelId}"]`) || 
                               document.querySelector(`[data-user-id="${recipientId}"]`);
          const unreadCountEl = document.getElementById(`unread-count-${recipientId}`) || 
                                (chatListItem && chatListItem.querySelector('.unread-count'));
          
          // Get count before clearing (for header update)
          let unreadCount = 0;
          if (unreadCountEl && !unreadCountEl.classList.contains('hidden')) {
            unreadCount = parseInt(unreadCountEl.textContent) || 0;
          }
          
          // Clear unread indicators for this chat
          if (chatListItem) {
            chatListItem.classList.remove("munread");
            const unreadChild = chatListItem.querySelector(".munread");
            if (unreadChild) unreadChild.classList.remove("munread");
          }
          
          // Reset individual unread count
          if (unreadCountEl) {
            unreadCountEl.textContent = '0';
            unreadCountEl.classList.add('hidden');
            unreadCountEl.classList.remove('flex');
          }
          
          // Update header counts (mobile and desktop)
          if (unreadCount > 0) {
            GlobalChatNotifications.updateHeaderUnreadCount(-unreadCount);
          }
        }
      };
      
      input.addEventListener("click", markChatAsRead, { once: true });
      input.addEventListener("focus", markChatAsRead, { once: true });
      input.dataset.readEventsBound = 'true';
    }
    
    // Send on Enter key
    if (input && !input.dataset.eventsBound) {
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          
          // Helper function to check if content is only empty tags (except for <img>)
          function isOnlyEmptyTagsOrWhitespaceOrImg(html) {
            // Remove all <img> tags
            let htmlWithoutImg = html.replace(/<img[\s\S]*?>/gi, '');
            // Remove all tags and check if anything left is not whitespace
            let textContent = htmlWithoutImg.replace(/<[^>]*>/g, '').trim();
            // If after removing tags (except img), nothing left, check if there was at least one <img>
            let hasImg = /<img[\s\S]*?>/gi.test(html);
            // If there is at least one <img>, allow (should publish)
            if (hasImg && textContent.length === 0) return false;
            // If only tags and whitespace, return true (should NOT publish)
            return textContent.length === 0;
          }

          const text = input.contentEditable ? input.textContent.trim() : input.value.trim();
          if (text.length === 0 && input.innerHTML.length === 0) {
            return;
          }
          
          // If only empty tags (except <img>), do not publish
          const content = input.contentEditable ? input.innerHTML : input.value;
          if (!isOnlyEmptyTagsOrWhitespaceOrImg(content)) {
            sendReply();
          }
          
          // Close emoji picker after sending with Enter
          const emojiContainer = notification.querySelector(`#chat_smiley_icons_container_${channelId}`);
          if (emojiContainer) {
            emojiContainer.style.display = 'none';
          }
        }
      });
      
      // Send typing status
      let typingTimeout = null;
      input.addEventListener('input', () => {
        if (globalChatSubscriptions[channelId]) {
          globalChatSubscriptions[channelId].sendTypingStatus(true);
          
          // Clear existing timeout
          if (typingTimeout) clearTimeout(typingTimeout);
          
          // Stop typing after 2 seconds of inactivity
          typingTimeout = setTimeout(() => {
            if (globalChatSubscriptions[channelId]) {
              globalChatSubscriptions[channelId].sendTypingStatus(false);
            }
          }, 2000);
        }
        
        // Replace emoji symbols with images as user types (for contentEditable)
        if (input.contentEditable) {
          // Save the current selection/caret position
          let selection = window.getSelection();
          let range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

          // Replace emoji symbols in the HTML with images
          let html = input.innerHTML;
          let replaced = false;

          // First detect URLs to avoid replacing emojis within them
          const urlRegex = /(https?:\/\/[^\s]+)/gi;
          let urlMatches = [];
          let match;

          // Find all URLs in the content
          while ((match = urlRegex.exec(html)) !== null) {
            urlMatches.push({
              url: match[0],
              start: match.index,
              end: match.index + match[0].length
            });
          }

          for (let symbol in emojiMap) {
            // Use regex to match the symbol not inside an <img> tag
            let regex = new RegExp(symbol.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1"), "g");
            if (regex.test(html)) {
              // Avoid double-replacing already replaced emojis
              html = html.replace(regex, function(match, offset) {
                // Check if this match is inside an <img> tag
                let before = html.slice(0, offset);
                let lastImg = before.lastIndexOf('<img');
                let lastClose = before.lastIndexOf('>');
                if (lastImg > -1 && lastImg > lastClose) {
                  return match; // inside <img>
                }

                // Check if this match is inside a URL
                for (let urlMatch of urlMatches) {
                  if (offset >= urlMatch.start && offset < urlMatch.end) {
                    return match; // inside URL, don't replace
                  }
                }

                replaced = true;
                return emojiMap[symbol];
              });
            }
          }
          if (replaced) {
            input.innerHTML = html;
            // Restore caret position to the end
            if (input.childNodes.length > 0) {
              let range = document.createRange();
              range.selectNodeContents(input);
              range.collapse(false);
              let sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }
        }
        
        // Update send button state
        this.updateSendButtonState(notification, channelId);
      });
      
      // Mark that events have been bound to prevent duplicate listeners
      input.dataset.eventsBound = 'true';
    }

    // Remove auto-hide functionality - user must manually close
  },
  
  updateSendButtonState: function(notification, channelId) {
    const input = notification.querySelector(`#chat_textarea_${channelId}`);
    const sendButton = notification.querySelector(`#publish_message_${channelId}`);

    if (!input || !sendButton) return;

    // Helper function to check if content is only empty tags (except for <img>)
    function isOnlyEmptyTagsOrWhitespaceOrImg(html) {
      let htmlWithoutImg = html.replace(/<img[\s\S]*?>/gi, '');
      let textContent = htmlWithoutImg.replace(/<[^>]*>/g, '').trim();
      let hasImg = /<img[\s\S]*?>/gi.test(html);
      if (hasImg && textContent.length === 0) return false;
      return textContent.length === 0;
    }

    const text = input.contentEditable ? input.textContent.trim() : input.value.trim();
    const content = input.contentEditable ? input.innerHTML : input.value;
    const isEmpty = (text.length === 0 && content.length === 0) || isOnlyEmptyTagsOrWhitespaceOrImg(content);

    if (isEmpty) {
      // Disable send button
      sendButton.disabled = true;
      sendButton.style.filter = "grayscale(1)";
      sendButton.style.opacity = "0.5";
      sendButton.style.cursor = "not-allowed";
    } else {
      // Enable send button
      sendButton.disabled = false;
      sendButton.style.filter = "grayscale(0)";
      sendButton.style.opacity = "1";
      sendButton.style.cursor = "pointer";
    }
  },

  initChatIcons: function(smiley_icon_id, smiley_div_container_id, textarea_id) {
    // Wait for DOM to be ready, then initialize
    setTimeout(() => {
      // Add smileys to container first
      this.addSmileys(smiley_div_container_id);
      
      // Ensure emoji picker starts hidden
      const emojiContainer = document.getElementById(smiley_div_container_id);
      if (emojiContainer) {
        emojiContainer.style.display = 'none';
      }
      
      // Set up click handlers after emojis are added
      this.onIconSmileyClick(smiley_icon_id, smiley_div_container_id);
      this.onSmileyClick(smiley_icon_id, smiley_div_container_id, textarea_id);
      this.onTextareaFocus(textarea_id, smiley_div_container_id);

      const textarea = document.getElementById(textarea_id);
      if (textarea && textarea.isContentEditable) {
        textarea.addEventListener('keyup', () => saveCaretPosition(textarea));
        textarea.addEventListener('mouseup', () => saveCaretPosition(textarea));
      }
    }, 100);
  },

  addSmileys: function(smiley_div_container_id) {
    const container = document.getElementById(smiley_div_container_id);
    if (!container) return;
    
    // Check if emojis are already added to prevent duplicates
    if (container.querySelector('.e-moji')) {
      return;
    }
   
    var smileys = "";
    emojis.set1.forEach(function(emoji) {
      smileys += `<span class="" tagstart="${emoji.symbol} " title="${emoji.symbol}">
        <img src="${emoji.image}" alt="${emoji.symbol}" loading="lazy" class="e-moji">
      </span>`;
    });

    container.insertAdjacentHTML("beforeend", smileys);
    
    // Add grid layout class
    container.classList.add('emoji-picker-grid');
    
    // Hover effects are handled by CSS
  },

  hideSmileys: function(smiley_div_container_id) {
    var smileyDiv = document.getElementById(smiley_div_container_id);
    if (smileyDiv) {
      smileyDiv.style.transition = "height 0.3s ease-out";
      smileyDiv.style.height = "0";
      setTimeout(() => {
        smileyDiv.style.display = "none";
      }, 300);
    }
  },

  onIconSmileyClick: function(smiley_icon_id, smiley_div_container_id) {
    const smileyIcon = document.getElementById(smiley_icon_id);
    if (smileyIcon) {
      // Remove existing listeners to prevent duplicates
      smileyIcon.removeEventListener('click', smileyIcon._clickHandler);
      
      // Create click handler
      const clickHandler = function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        const smileyDiv = document.getElementById(smiley_div_container_id);
        if (smileyDiv) {
          const isVisible = smileyDiv.style.display === 'grid';
          
          if (isVisible) {
            // Close the picker
            smileyDiv.style.display = 'none';
          } else {
            // Position and show the picker
            GlobalChatNotifications.positionEmojiPicker(smileyIcon, smileyDiv);
            smileyDiv.style.display = 'grid';
            smileyDiv.style.overflow = "hidden";
            smileyDiv.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
            // Set height to 0 only if not already open
            smileyDiv.style.height = "0";
            void smileyDiv.offsetWidth;
            // Now set to scrollHeight
            var targetHeight = smileyDiv.scrollHeight + "px";
            smileyDiv.style.height = targetHeight;
            smileyDiv.addEventListener("transitionend", function handler() {
              smileyDiv.style.height = "auto";
              smileyDiv.removeEventListener("transitionend", handler);
              setTimeout(() => {
                smileyDiv.style.overflow = "visible";
              }, 100);
            });
          }
        }
      };
      
      // Store and add the handler
      smileyIcon._clickHandler = clickHandler;
      smileyIcon.addEventListener('click', clickHandler);
    }
  },

  onSmileyClick: function(smiley_icon_id, smiley_div_container_id, textarea_id) {
    // Use event delegation to handle clicks on dynamically added emoji spans
    const smileyIcon = document.getElementById(smiley_icon_id);
    const smileyContainer = document.getElementById(smiley_div_container_id);
    if (smileyContainer) {
      // Remove any existing listeners to avoid duplicates
      smileyContainer.removeEventListener('click', smileyContainer._clickHandler);
      
      // Create the click handler function
      const clickHandler = function(e) {
        // Check if clicked element is an emoji span
        let target = e.target;
        
        // If clicked on img inside span, get the parent span
        if (target.tagName === 'IMG' && target.parentElement.tagName === 'SPAN') {
          target = target.parentElement;
        }
        
        // If it's a span with tagstart attribute (emoji)
        if (target.tagName === 'SPAN' && target.getAttribute('tagstart')) {
          e.stopPropagation();
          // Use innerHTML like in chat.js instead of tagstart attribute
          const value = target.innerHTML.trim();
          const textarea = document.getElementById(textarea_id);
          if (textarea) {
            // For contentEditable divs, use innerHTML
            if (textarea.isContentEditable) {
              // Restore caret if we saved one
              textarea.focus();
              let selection = window.getSelection();
              selection.removeAllRanges();
            
              if (lastRange) {
                selection.addRange(lastRange);
              } else {
                // Fallback to end if nothing saved
                const range = document.createRange();
                range.selectNodeContents(textarea);
                range.collapse(false);
                selection.addRange(range);
              }
            
              const range = selection.getRangeAt(0);
              range.deleteContents();
            
              // Create emoji fragment
              const temp = document.createElement('div');
              temp.innerHTML = " " + value + " ";
              const frag = document.createDocumentFragment();
              let node, lastNode;
              while ((node = temp.firstChild)) {
                lastNode = frag.appendChild(node);
              }
            
              range.insertNode(frag);
            
              // Move caret after emoji
              if (lastNode) {
                range.setStartAfter(lastNode);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
              }
            } else {
              // For regular input fields, add to value
              const cursorPos = textarea.selectionStart || textarea.value.length;
              const textBefore = textarea.value.substring(0, cursorPos);
              const textAfter = textarea.value.substring(cursorPos);
              textarea.value = textBefore + value + " " + textAfter;
              // Set cursor position after the emoji
              const newPos = cursorPos + value.length + 1;
              textarea.setSelectionRange(newPos, newPos);
            }
            textarea.focus(); // Focus back to input

            // Update send button state after adding emoji
            const channelId = textarea_id.split("_")[2];
            const notification = document.querySelector(`[data-channel-id="${channelId}"]`);
            if (notification) {
              GlobalChatNotifications.updateSendButtonState(notification, channelId);
            }
          }
          // Keep picker open - don't close when selecting emojis
          // Picker will only close when clicking outside or on text area
        }
      };
      
      // Store the handler reference for later removal
      smileyContainer._clickHandler = clickHandler;
      
      // Add the event listener
      smileyContainer.addEventListener('click', clickHandler);
      
      // Add click outside handler to hide picker
      // Only close when clicking outside the emoji picker and emoji button
      document.addEventListener('click', function(e) {
        if (typeof smileyIcon === 'undefined' || typeof smileyContainer === 'undefined' || !smileyIcon || !smileyContainer) {
          return;
        }
        if (!smileyIcon.contains(e.target) && !smileyContainer.contains(e.target)) {
          smileyContainer.style.display = 'none';
        }
      });
    }
  },

  onTextareaFocus: function(textarea_id, smiley_div_container_id) {
    // Handle placeholder for contentEditable and hide emoji picker on focus
    const textarea = document.getElementById(textarea_id);
    const emojiContainer = document.getElementById(smiley_div_container_id);
    
    if (textarea) {
      textarea.addEventListener('focus', function() {
        // Hide emoji picker when textarea is focused
        if (emojiContainer) {
          emojiContainer.style.display = 'none';
        }
        
        // Handle placeholder for contentEditable
        if (this.contentEditable && this.textContent.trim() === '') {
          this.classList.remove('placeholder-active');
        }
      });
      
      textarea.addEventListener('blur', function() {
        // Handle placeholder for contentEditable
        if (this.contentEditable && this.textContent.trim() === '') {
          this.classList.add('placeholder-active');
        }
      });
      
      textarea.addEventListener('input', function() {
        // Handle placeholder for contentEditable
        if (this.contentEditable) {
          if (this.textContent.trim() === '') {
            this.classList.add('placeholder-active');
          } else {
            this.classList.remove('placeholder-active');
          }
        }
      });
    }
  },
  
  positionEmojiPicker: function(iconElement, pickerElement) {
    if (!iconElement || !pickerElement) return;
    
    const iconRect = iconElement.getBoundingClientRect();
    // Position above the input area, centered horizontally
    const left = iconRect.left - (240 / 2) + (iconRect.width / 2); // Center on icon, 240px is picker width
    const top = iconRect.top - 250; // Position well above the input area
    
    // Ensure picker doesn't go off screen
    const finalLeft = Math.max(10, Math.min(left, window.innerWidth - 250)); // Keep 10px margin from edges
    const finalTop = Math.max(10, top); // Keep 10px margin from top
    
    pickerElement.classList.add('emoji-picker-fixed');
    pickerElement.style.left = finalLeft + 'px';
    pickerElement.style.top = finalTop + 'px';
    pickerElement.style.position = 'fixed';
    pickerElement.style.zIndex = '10000';
  },
  
  checkUserOnlineStatus: function(userId) {
    // Check if we're on the chat page and can get status from DOM
    if (window.location.pathname.includes('/chats')) {
      const chatItem = document.querySelector(`[data-user-id="${userId}"]`);
      if (chatItem) {
        return chatItem.dataset.userStatus === 'true';
      }
    }

    // Default to true if we can't determine status as the user sending the message will always be online
    return true;
  },
  
  removeNotification: function(channelId) {
    const notificationData = activeNotifications.get(channelId);
    if (!notificationData) return;

    const notification = notificationData.element;
    notification.style.animation = 'slideOutRight 0.3s ease-in forwards';

    // Clear tracked unread count for this channel (user dismissed without reading)
    if (this.channelUnreadCounts && this.channelUnreadCounts[channelId]) {
      delete this.channelUnreadCounts[channelId];
    }

    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      activeNotifications.delete(channelId);
    }, 300);
  },

  showSendError: function(channelId, failedMessage) {
    const notificationData = activeNotifications.get(channelId);
    if (!notificationData) return;

    const messagesContainer = notificationData.element.querySelector('.chat-notification-messages');
    if (messagesContainer) {
      // Show error message in chat
      const errorHTML = `
        <div class="chat-message-wrapper error">
          <div class="chat-message-bubble error" style="background-color: #fee2e2; border: 1px solid #ef4444; color: #991b1b;">
            <div class="chat-message-content" style="font-size: 12px;">
              ${I18n.t('message_send_failed') || 'Failed to send message. Please try again.'}
            </div>
          </div>
        </div>
      `;
      messagesContainer.insertAdjacentHTML('beforeend', errorHTML);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;

      // Restore the failed message to input so user can retry
      const input = notificationData.element.querySelector(`#chat_textarea_${channelId}`);
      if (input) {
        if (input.contentEditable) {
          input.innerHTML = failedMessage;
        } else {
          input.value = failedMessage;
        }
        // Update send button state
        this.updateSendButtonState(notificationData.element, channelId);
      }
    }
  },

  loadChatHistory: function(channelId, senderData) {
    // Use the Rails proxy endpoint to avoid CORS issues
    const params = new URLSearchParams({
      sender_id: window.GLOBAL_CHAT.senderChatId,
      channel_id: channelId,
      fields: 'message_html,created_at,sender{app_user_id,name}',
      page: 1
    }).toString();

    fetch(`/proxy_chat_messages?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      credentials: "same-origin"
    })
    .then(response => response.json())
    .then(response => {
      const messages = response["messages"];
      if (messages && messages.length > 0) {
        // Clear the initial message and replace with history
        const notificationData = activeNotifications.get(channelId);
        if (notificationData) {
          const messagesContainer = notificationData.element.querySelector('.chat-notification-messages');
          messagesContainer.innerHTML = ''; // Clear existing messages
          
          // Add all previous messages (reverse order to show oldest first)
          messages.reverse().forEach((item) => {
            if (item.sender) {
              const messageType = item.sender.app_user_id == window.GLOBAL_CHAT.senderId ? 'sent' : 'received';
              const time = this.formatTime(item.created_at);
              
              const messageHTML = `
                <div class="chat-message-wrapper ${messageType}">
                  <div class="chat-message-bubble ${messageType}">
                    <div class="chat-message-content">
                      ${sanitizeMessageHTML(item.message_html)}
                    </div>
                    <div class="chat-message-meta">${time}</div>
                  </div>
                </div>
              `;
              messagesContainer.insertAdjacentHTML('beforeend', messageHTML);
            }
          });
          
          // Scroll to bottom
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
          
          // Ensure footer remains visible and functional
          const footer = notificationData.element.querySelector('.chat-notification-footer');
          const input = notificationData.element.querySelector('.chat-notification-input');
          const sendBtn = notificationData.element.querySelector('.chat-notification-send');
          
          if (footer) {
            footer.style.display = 'flex';
            footer.style.visibility = 'visible';
            // Footer visibility ensured
          }
          
          if (input) {
            // Input field present and visible
          } else {
            // Input field missing after loading history
          }
          
          if (sendBtn) {
            // Send button present and visible
          } else {
            // Send button missing after loading history
          }
          
          // Re-bind events if needed (in case DOM was affected)
          const notificationId = notificationData.element.id;
          if (input && sendBtn) {
            // Check if events are still bound by checking for existing listeners
            const testClick = sendBtn.onclick;
            if (!testClick) {
              // Re-binding events after history load
              GlobalChatNotifications.bindNotificationEvents(notificationData.element, notificationId, channelId);
            }
          }
        }
      }
    })
    .catch(error => {
      // Error loading chat history
    });
  },
  
  formatTime: function(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = 'ru'; // Match default in chat_cable.js
    const formatTime = (d) => {
      const options = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: userTimeZone,
      };
      return new Intl.DateTimeFormat(locale, options).format(d);
    };
    const isSameDate = (d1, d2) => {
      const _d1 = new Date(d1.toLocaleString('en-US', { timeZone: userTimeZone }));
      const _d2 = new Date(d2.toLocaleString('en-US', { timeZone: userTimeZone }));
      return _d1.getFullYear() === _d2.getFullYear() &&
        _d1.getMonth() === _d2.getMonth() &&
        _d1.getDate() === _d2.getDate();
    };
    const isYesterday = (d, n) => {
      const yest = new Date(n.toLocaleString('en-US', { timeZone: userTimeZone }));
      yest.setDate(yest.getDate() - 1);
      return isSameDate(d, yest);
    };
    if (isSameDate(date, now)) {
      return formatTime(date);
    } else if (isYesterday(date, now)) {
      return I18n.t('yesterday');
    } else {
      const day = date.getDate();
      const month = I18n.t(`date.month_names`)[date.getMonth() + 1];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    }
  },
  
  subscribeToChannel: function(channelId, recipientId) {
    if (globalChatSubscriptions[channelId]) return; // Already subscribed
    
    globalChatSubscriptions[channelId] = globalConsumer.subscriptions.create(
      { 
        channel: "ChatChannel",
        channel_id: channelId
      },
      {
        connected() {
          // Connected to global chat channel
        },
        
        disconnected() {
          // Disconnected from global chat channel
        },
        
        received(data) {
          // Handle received messages for notifications
          if (data.type === 'message') {
            const senderData = {
              id: data.sender.id,
              name: data.sender.name,
              image_url: data.sender.image_url,
              online: data.sender.online || data.online || GlobalChatNotifications.checkUserOnlineStatus(data.sender.id)
            };
            
            // Determine if message is sent or received
            const messageType = data.sender.id == window.GLOBAL_CHAT.senderId ? 'sent' : 'received';
            
            // Show message in notification
            GlobalChatNotifications.showNotification(senderData, data.message || data.message_raw, channelId, messageType);
            
            // Broadcast message to main chat window if it's open
            if (window.location.pathname.includes('/chats')) {
              window.dispatchEvent(new CustomEvent('chat-message-received', {
                detail: {
                  channelId: channelId,
                  message: data.message || data.message_raw,
                  sender: data.sender,
                  messageType: messageType
                }
              }));
            }
          } else if (data.type === 'typing' || data.type === 'typing_status') {
            // Handle typing status in popup
            const notificationData = activeNotifications.get(channelId);
            if (notificationData && data.user_id != window.GLOBAL_CHAT.senderId) {
              const typingDiv = notificationData.element.querySelector('.chat-notification-typing');
              if (typingDiv) {
                if (data.is_typing) {
                  typingDiv.querySelector('.typing-text').textContent = `${data.user || 'User'} ${I18n.t('is_typing') || 'is typing'}...`;
                  typingDiv.style.display = 'block';
                  
                  // Auto-hide after 3 seconds
                  clearTimeout(notificationData.typingTimeout);
                  notificationData.typingTimeout = setTimeout(() => {
                    typingDiv.style.display = 'none';
                  }, 3000);
                } else {
                  typingDiv.style.display = 'none';
                }
              }
            }
            
            // Broadcast to main chat window if it's open
            if (window.location.pathname.includes('/chats')) {
              window.dispatchEvent(new CustomEvent('chat-typing-status', {
                detail: {
                  channelId: channelId,
                  isTyping: data.is_typing,
                  userId: data.user_id,
                  user: data.user
                }
              }));
            }
          }
        },
        
        sendMessage(message, platform) {
          this.perform('send_message', { 
            message: message,
            platform: platform || 'web'
          });
        },
        
        sendTypingStatus(isTyping) {
          this.perform('typing_status', { is_typing: isTyping });
        },
        
        markAsRead(messageId) {
          this.perform('mark_as_read', { message_id: messageId });
        }
      }
    );
  },
  
  subscribeToUserChannel: function(senderChatId) {
    if (globalUserSubscription) return;
    
    globalUserSubscription = globalConsumer.subscriptions.create(
      { 
        channel: "UserChannel",
        user_id: senderChatId 
      },
      {
        connected() {
          // Connected to global user notification channel
        },
        
        disconnected() {
          // Disconnected from global user notification channel
        },
        
        received(data) {
          if (data.type === 'new_message_notification') {
            GlobalChatNotifications.handleNewMessageNotification(data);
          }
        }
      }
    );
  },
  
  handleNewMessageNotification: function(data) {
    const isOnChatPage = window.location.pathname.includes('/chats');
    
    // On chat pages, show notifications only for non-active conversations
    if (isOnChatPage) {
      const activeChat = document.querySelector('.active');
      if (activeChat && activeChat.dataset.channelId === data.channel_id) {
        // This is the active conversation, don't show popup
        return;
      }
      // For non-active conversations on /chats page, continue to show notifications
    }
    
    // Update header unread count in real-time (only on non-chat pages, chat_cable.js handles /chats)
    if (!isOnChatPage) {
      this.updateHeaderUnreadCount(1);
      
      // Track unread count per channel for proper decrement when user reads messages
      this.channelUnreadCounts = this.channelUnreadCounts || {};
      this.channelUnreadCounts[data.channel_id] = (this.channelUnreadCounts[data.channel_id] || 0) + 1;
    }

    // Show notification popup for new messages (initial notification)
    const senderData = {
      id: data.sender_id,
      name: data.sender_name,
      image_url: data.sender_image_url,
      online: data.sender_online || data.online || GlobalChatNotifications.checkUserOnlineStatus(data.sender_id)
    };

    // Subscribe to the channel if not already subscribed
    // Subsequent messages will be handled by ChatChannel.received()
    const isNewSubscription = !globalChatSubscriptions[data.channel_id];
    if (isNewSubscription) {
      this.subscribeToChannel(data.channel_id, data.sender_id);
    }

    // Only show notification from UserChannel for:
    // 1. New subscriptions (first message won't come through ChatChannel)
    // 2. Or if notification popup doesn't exist yet for this channel
    // This prevents duplicates since ChatChannel.received() also calls showNotification()
    if (isNewSubscription || !activeNotifications.has(data.channel_id)) {
      this.showNotification(senderData, data.message_preview, data.channel_id);
    }

    // Blink title for notification
    if (document.hidden) {
      this.blinkTitle(data.sender_name);
    }
  },
  
  showNotification: function(senderData, message, channelId = null, messageType = 'received') {
    // On chat pages, show notifications only for non-active conversations
    if (window.location.pathname.includes('/chats')) {
      const activeChat = document.querySelector('.active');
      if (activeChat && activeChat.dataset.channelId === channelId) {
        // This is the active conversation, don't show popup
        return;
      }
      // For non-active conversations on /chats page, continue to show notifications
    }
    
    const notificationId = `chat-notification-${channelId}`;
    
    // Create or update notification
    if (activeNotifications.has(channelId)) {
      this.updateNotification(channelId, message, messageType);
    } else {
      this.createNotification(notificationId, senderData, message, channelId, messageType);
    }
  },
  
  blinkTitle: function(sender) {
    const originalTitle = document.title;
    const newTitle = `💬 ${sender} sent you a message`;
    
    let blinkCount = 0;
    const blinkInterval = setInterval(() => {
      document.title = blinkCount % 2 === 0 ? newTitle : originalTitle;
      blinkCount++;
      
      if (blinkCount > 10 || !document.hidden) {
        document.title = originalTitle;
        clearInterval(blinkInterval);
      }
    }, 1000);
    
    // Stop blinking when user focuses on page
    const stopBlinking = () => {
      document.title = originalTitle;
      clearInterval(blinkInterval);
      document.removeEventListener('visibilitychange', stopBlinking);
    };
    
    document.addEventListener('visibilitychange', stopBlinking);
  },
  
  updateHeaderUnreadCount: function(delta) {
    // Update header chat count badges (mobile and desktop)
    const updateBadge = (countId, containerId) => {
      const countEl = document.getElementById(countId);
      const containerEl = document.getElementById(containerId);
      if (countEl && containerEl) {
        const current = parseInt(countEl.textContent) || 0;
        const updated = Math.max(0, current + delta);
        countEl.textContent = updated;
        containerEl.classList.toggle('hidden', updated === 0);
      }
    };
    
    // Mobile header badge
    updateBadge('header_chat_count', 'header_chat_count_container');
    // Desktop header badge
    updateBadge('header_chat_count_desktop', 'header_chat_count_container_desktop');
  },
  
  isMobileDevice: function() {
    // Use the same logic as the Rails mobile_device? helper method
    const userAgent = navigator.userAgent;
    return (userAgent.match(/Mobile|webOS|Opera Mini/i) && !userAgent.match(/iPad/i));
  }
};

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  GlobalChatNotifications.init();
});

// Clean up on page unload
window.addEventListener('beforeunload', function() {
  // Clear all active notifications
  activeNotifications.forEach((notificationData, channelId) => {
    GlobalChatNotifications.removeNotification(channelId);
  });
  
  // Disconnect all subscriptions
  for (let channelId in globalChatSubscriptions) {
    if (globalChatSubscriptions[channelId]) {
      globalChatSubscriptions[channelId].unsubscribe();
    }
  }
  
  if (globalUserSubscription) {
    globalUserSubscription.unsubscribe();
  }
  
  if (globalConsumer) {
    globalConsumer.disconnect();
  }
});

export default GlobalChatNotifications;